/**
 * Background Tasks — Type Definitions
 *
 * Re-exports schema types from @segmently/schema-models/strategy
 * and defines local configuration types for filtering, UI variants, etc.
 */

// ── Schema types (canonical, generated from openapi-strategy.yaml) ──
export type {
  BackendTask,
  TaskStatus,
  AgentTaskType,
  TargetEntityPath,
  TaskPhaseDefinition,
  TaskPhaseStatus,
  TaskPhaseProgress,
  TaskTokenUsage,
  TaskEvalLogEntry,
  TaskValidationIssue,
  TaskValidationResult,
} from '@segmently/schema-models/strategy';

export {
  BackendTaskKeySourceEnum,
} from '@segmently/schema-models/strategy';

import type { TaskStatus } from '@segmently/schema-models/strategy';
import type { ReactNode } from 'react';

// ── Status groups for UI filter chips ──

export const ACTIVE_STATUSES: TaskStatus[] = ['pending', 'running', 'retrying', 'cancelling'];
export const TERMINAL_STATUSES: TaskStatus[] = ['completed', 'error', 'cancelled', 'skipped'];

// ── Filter & UI configuration types ──

export type StatusFilterMode = 'active' | 'completed' | 'all';
export type OwnerFilterMode = 'mine' | 'all';

export interface TaskFilter {
  /** Filter by specific status(es) */
  status?: TaskStatus | TaskStatus[];
  /** Convenience mode: active = running+pending+retrying, completed = completed+error+cancelled */
  statusMode?: StatusFilterMode;
  /** Filter by task type(s) */
  taskType?: string | string[];
  /** Filter by strategy (entity scoping) */
  strategyId?: string;
  /** Filter by content-plan author workspace */
  authorId?: string;
  /** Filter by block (entity scoping) */
  blockId?: string;
  /** Filter by screen — used by the V2 screen editor dialog's in-dialog tasks panel.
   * Note: not part of the canonical BackendTask interface; image-generation tasks
   * write `screenId` as a top-level field on the Firestore doc via AgentController.generateImage.
   * The store's applyFilter() reads it via runtime cast. */
  screenId?: string;
  /** 'mine' = current userId only, 'all' = all project members */
  ownerMode?: OwnerFilterMode;
  /** Explicit userId filter (ownerMode uses this internally) */
  userId?: string;
  /** true = only show tasks where current user NOT in viewedBy */
  unviewedOnly?: boolean;
  /** Sort field */
  sortBy?: 'createdAt' | 'updatedAt';
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
}

export type TaskPanelVariant = 'floating' | 'inline' | 'sidebar';
export type TaskRowActionsRenderer = (task: import('@segmently/schema-models/strategy').BackendTask) => ReactNode;

export interface TaskPanelConfig {
  /** Panel layout variant */
  variant: TaskPanelVariant;
  /** Task filter (merged with store's statusFilter/ownerFilter) */
  filter?: TaskFilter;
  /** Maximum tasks to display */
  maxItems?: number;
  /** Show "go to result" button on completed tasks */
  showNavigation?: boolean;
  /** Show In Progress / Completed / All toggle */
  showStatusFilter?: boolean;
  /** Show My Tasks / All Members toggle */
  showOwnerFilter?: boolean;
  /** Show unviewed count badge */
  showUnviewedBadge?: boolean;
  /** Callback when task row is clicked */
  onTaskClick?: (task: import('@segmently/schema-models/strategy').BackendTask) => void;
  /** Optional module-specific row actions, rendered next to navigation/remove buttons. */
  renderRowActions?: TaskRowActionsRenderer;
}
